import { RefObject, useLayoutEffect, useState } from "react";

/**
 * useFocusedWithin
 *
 * Returns whether focus is within the given element or any of its descendants.
 *
 * @param ref - Ref to the element to check for focus within
 * @param shouldConsiderFocused - Optional function to check if focus should be considered "within" even when outside the element (e.g., for portals)
 * @returns boolean indicating if focus is within the element
 */
export function useFocusedWithin(
  ref: RefObject<HTMLElement>,
  shouldConsiderFocused?: (focusedElement: HTMLElement | null) => boolean,
): boolean {
  const [isFocusedWithin, setIsFocusedWithin] = useState(false);

  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return;

    const checkFocus = () => {
      const activeElement = document.activeElement as HTMLElement | null;
      const isWithin =
        (activeElement && element.contains(activeElement)) ||
        (shouldConsiderFocused?.(activeElement) ?? false);

      setIsFocusedWithin(isWithin);
    };

    // Check immediately
    checkFocus();

    // Also check after a short delay to catch any async focus changes
    const timeoutId = setTimeout(checkFocus, 0);

    document.addEventListener("focusin", checkFocus, true);
    document.addEventListener("focusout", checkFocus, true);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener("focusin", checkFocus, true);
      document.removeEventListener("focusout", checkFocus, true);
    };
  }, [ref, shouldConsiderFocused]);

  return isFocusedWithin;
}
